<template>
	<div class="tabletWide:py-48 desktop:py-56 tabletWide:grid tabletWide:grid-cols-12 tabletWide:gap-16 tabletWide:items-center">
		<div class="tabletWide:col-span-6 tabletWide:row-start-1 tabletWide:col-start-7">
			<layoutPost type="center" class="space-y-32">
				<slot name="title">
					<h2 class="font-bold text-32 tablet:text-40 desktop:text-48 whitespace-nowrap">GO all across NZ</h2>
				</slot>

				<slot name="content">
					<p class="text-18 desktop:text-22">We have nine car rental branches spread evenly throughout New Zealand, in the major destinations of Auckland, Wellington, Christchurch, Nelson, Queenstown, Dunedin and Invercargill so that you’re never far from a GO rental car.</p>
				</slot>

				<div class="relative space-y-24">
					<ul v-if="island" class="relative z-10 flex-grow">
						<li v-for="location in locations" :key="location.code" class="flex space-x-12 items-center leading-16 py-10">
							<svg-icon name="dot" class="text-goPink iconSize-16" />
							<nuxtLink :to="location.to" class="text-goPink hover:text-goPinkDark active:text-goPinkDarker font-bold">{{ location.name }}</nuxtLink>
						</li>
					</ul>
					<div v-else class="relative z-10 flex-grow grid desktopWide:grid-cols-2 gap-y-24 gap-x-16">
						<div v-for="item in locationIslands" :key="item.name">
							<h3 class="mb-8">
								<nuxtLink :to="`/rental-car-locations/${item.name}-island-new-zealand`" class="text-20 text-goPink hover:text-goPinkDark active:text-goPinkDarker font-bold">{{ item.title }}</nuxtLink>
							</h3>
							<ul>
								<li v-for="location in item.locations" :key="location.code" class="flex space-x-12 items-center leading-16 py-10">
									<svg-icon name="dot" class="text-goPink iconSize-16" />
									<nuxtLink :to="location.to" class="text-goPink hover:text-goPinkDark active:text-goPinkDarker font-bold">{{ location.name }}</nuxtLink>
									<span v-if="tagLocations && tagLocations.locations.includes(location.code)" class="text-goGrayDark">{{ tagLocations.label }}</span>
								</li>
							</ul>
						</div>
					</div>

					<slot v-if="'extraContent' in $scopedSlots" name="extraContent" />

					<picture class="tabletWide:hidden absolute h-full top-0 right-0 tablet:-right-56">
						<img src="/images/blank.svg" :data-src="mapImage" alt="Map of New Zealand" decoding="async" class="lazyload w-full h-full object-right object-contain tabletWide:object-center tabletWide:object-cover">
					</picture>
				</div>
			</layoutPost>
		</div>

		<div class="hidden tabletWide:unset tabletWide:col-span-6">
			<div class="relative tablet:pl-48">
				<picture class="top-0 w-full h-full">
					<img src="/images/blank.svg" :data-src="mapImage" alt="Map of New Zealand" decoding="async" class="lazyload w-full h-full">
				</picture>

				<ul class="absolute top-0 w-full h-full z-10">
					<li v-for="location in locations" :key="location.code" class="flex items-center leading-16 absolute w-6/12 font-bold text-14 text-goPink hover:text-goPinkDark active:text-goPinkDarker" :class="location.labelPosition === 'left' && 'flex-row-reverse text-right'" :style="island ? location.partMapPointStyle : location.allMapPointStyle">
						<svg-icon name="dot" class="iconSize-24 mx-12" />
						<nuxtLink :to="location.to">
							<span v-if="location.isNowrapLabel">{{ location.name }}</span>
							<span v-else class="flex flex-col">
								<span v-for="word in splitBranchNameWords(location.name)" :key="word">{{ word }}</span>
							</span>
						</nuxtLink>
					</li>
				</ul>
			</div>
		</div>

		<div v-if="'footer' in $scopedSlots" class="col-span-12 p-16 tablet:p-48 tablet:pr-0 tabletWide:p-0 tabletWide:pt-56 tabletWide:pl-56 space-y-32">
			<slot name="footer" />
		</div>
	</div>
</template>

<script>
const islands = ['north', 'south']

export default {
	components: {
		layoutPost: () => import('~/components/layoutPost.vue'),
	},
	props: {
		island: {
			type: String,
			default: null,
			validator: island => islands.includes(island)
		},
		tagLocations: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			branches: [
				{
					code: 'AKLC',
					name: 'Auckland City',
					island: 'north',
					to: '/rental-car-locations/auckland-city/',
					allMapPointStyle: { top: '18.5%', left: '57.5%' },
					partMapPointStyle: { top: '32.5%', left: '29.5%' },
					labelPosition: 'right',
					isNowrapLabel: true,
				},
				{
					code: 'AKLA',
					name: 'Auckland Airport',
					island: 'north',
					to: '/rental-car-locations/auckland-airport/',
					allMapPointStyle: { top: '19%', left: '12.9%' },
					partMapPointStyle: { top: '34%', left: '-13%' },
					labelPosition: 'left',
					isNowrapLabel: false,
				},
				{
					code: 'WKE',
					name: 'Waiheke Island',
					island: 'north',
					to: '/rental-car-locations/waiheke/',
					allMapPointStyle: { top: '17%', left: '60%' },
					partMapPointStyle: { top: '30%', left: '34%' },
					labelPosition: 'right',
					isNowrapLabel: true,
				},
				{
					code: 'WLG',
					name: 'Wellington Airport',
					island: 'north',
					to: '/rental-car-locations/wellington/',
					allMapPointStyle: { top: '52%', left: '58%' },
					partMapPointStyle: { top: '93.5%', left: '30%' },
					labelPosition: 'right',
					isNowrapLabel: false,
				},
				{
					code: 'NSN',
					name: 'Nelson Airport',
					island: 'south',
					to: '/rental-car-locations/nelson/',
					allMapPointStyle: { top: '52%', left: '1%' },
					partMapPointStyle: { top: '9%', left: '32.5%' },
					labelPosition: 'left',
					isNowrapLabel: false,
				},
				{
					code: 'CHC',
					name: 'Christchurch Airport',
					island: 'south',
					to: '/rental-car-locations/christchurch/',
					allMapPointStyle: { top: '69%', left: '39.2%' },
					partMapPointStyle: { top: '41%', left: '67%' },
					labelPosition: 'right',
					isNowrapLabel: false,
				},
				{
					code: 'ZQN',
					name: 'Queenstown Airport',
					island: 'south',
					to: '/rental-car-locations/queenstown/',
					allMapPointStyle: { top: '81%', left: '-29.5%' },
					partMapPointStyle: { top: '62%', left: '-19%' },
					labelPosition: 'left',
					isNowrapLabel: false,
				},
				{
					code: 'DUD',
					name: 'Dunedin Airport',
					island: 'south',
					to: '/rental-car-locations/dunedin/',
					allMapPointStyle: { top: '88.1%', left: '21.7%' },
					partMapPointStyle: { top: '76.5%', left: '39%' },
					labelPosition: 'right',
					isNowrapLabel: false,
				},
				{
					code: 'IVC',
					name: 'Invercargill Airport',
					island: 'south',
					to: '/rental-car-locations/invercargill-airport/',
					allMapPointStyle: { top: '92.1%', left: '11.7%' },
					partMapPointStyle: { top: '84%', left: '19%' },
					labelPosition: 'right',
					isNowrapLabel: false,
				},
			],
		}
	},
	computed: {
		locations() {
			return this.island
				? this.branches.filter(branch => branch.island === this.island)
				: this.branches
		},
		locationIslands() {
			return islands.map(island => ({
				name: island,
				title: `${island[0].toUpperCase() + island.slice(1)} Island`,
				locations: this.branches.filter(branch => branch.island === island)
			}))
		},
		mapImage() {
			return this.island ? `/images/${this.island}-island-map.png` : '/images/nzMap.png'
		}
	},
	methods: {
		splitBranchNameWords(name) {
			return name.split(' ')
		}
	}
}
</script>
